import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { FaChevronDown, FaBars, FaTimes } from 'react-icons/fa';

function Header() {
  const [openMenu, setOpenMenu] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  useEffect(() => {
    function handleClickOutside(event) {
      if (headerRef.current && !headerRef.current.contains(event.target)) setOpenMenu(null);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "auto";
    return () => { document.body.style.overflow = "auto"; };
  }, [isMobileMenuOpen]);

  const handleToggleMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const handleLinkClick = () => {
    setIsMobileMenuOpen(false);
    setOpenMenu(null);
  };
  const handleDropdownClick = (menu) => setOpenMenu(openMenu === menu ? null : menu);

  const navLinkClass = "flex items-center gap-1 text-sm font-semibold leading-6 text-dark-text-primary transition-colors hover:text-dark-text-secondary";
  const dropdownLinkClass = "block rounded-lg p-3 text-sm leading-6 text-dark-text-primary transition-colors hover:bg-dark-card";

  const renderNavLinks = () => (
    <>
      <div className="relative">
        <button onClick={() => handleDropdownClick("platform")} className={navLinkClass}>
          Platform <FaChevronDown size={12} className={`transition-transform duration-300 ${openMenu === 'platform' ? 'rotate-180' : ''}`} />
        </button>
        {openMenu === "platform" && (
          <div className="absolute -left-8 top-full z-10 mt-5 w-screen max-w-md overflow-hidden rounded-2xl bg-dark-card/80 backdrop-blur-xl shadow-2xl ring-1 ring-white/10">
            <div className="p-4 grid grid-cols-2 gap-x-6 gap-y-2">
              <div>
                <h4 className="px-3 pb-1 text-xs font-bold uppercase text-dark-text-secondary">Core Features</h4>
                <Link to="/scheduling" onClick={handleLinkClick} className={dropdownLinkClass}>Scheduling</Link>
                <Link to="/analytics" onClick={handleLinkClick} className={dropdownLinkClass}>Analytics</Link>
                <Link to="/engagements" onClick={handleLinkClick} className={dropdownLinkClass}>Engagements</Link>
                <Link to="/ai-automation" onClick={handleLinkClick} className={dropdownLinkClass}>AI & Automation</Link>
              </div>
              <div>
                <h4 className="px-3 pb-1 text-xs font-bold uppercase text-dark-text-secondary">By Platform</h4>
                <Link to="/instagram" onClick={handleLinkClick} className={dropdownLinkClass}>Instagram</Link>
                <Link to="/facebook" onClick={handleLinkClick} className={dropdownLinkClass}>Facebook</Link>
                <Link to="/twitter" onClick={handleLinkClick} className={dropdownLinkClass}>Twitter</Link>
                <Link to="/linkedin" onClick={handleLinkClick} className={dropdownLinkClass}>LinkedIn</Link>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="relative">
        <button onClick={() => handleDropdownClick("solutions")} className={navLinkClass}>
          Solutions <FaChevronDown size={12} className={`transition-transform duration-300 ${openMenu === 'solutions' ? 'rotate-180' : ''}`} />
        </button>
        {openMenu === "solutions" && (
            <div className="absolute -left-8 top-full z-10 mt-5 w-72 rounded-2xl bg-dark-card/80 backdrop-blur-xl shadow-2xl ring-1 ring-white/10 p-2">
                 <Link to="/manage-social-media" onClick={handleLinkClick} className={dropdownLinkClass}>Manage Social Media</Link>
                 <Link to="/gather-deep-insights" onClick={handleLinkClick} className={dropdownLinkClass}>Gather Deep Insights</Link>
                 <Link to="/drive-more-sales" onClick={handleLinkClick} className={dropdownLinkClass}>Drive More Sales</Link>
                 <Link to="/brand-awareness" onClick={handleLinkClick} className={dropdownLinkClass}>Brand Awareness</Link>
            </div>
        )}
      </div>
      <div className="relative">
        <button onClick={() => handleDropdownClick("tools")} className={navLinkClass}>
          Mini Tools <FaChevronDown size={12} className={`transition-transform duration-300 ${openMenu === 'tools' ? 'rotate-180' : ''}`} />
        </button>
        {openMenu === "tools" && (
            <div className="absolute -left-8 top-full z-10 mt-5 w-72 rounded-2xl bg-dark-card/80 backdrop-blur-xl shadow-2xl ring-1 ring-white/10 p-2">
                 <Link to="/bg-remover" onClick={handleLinkClick} className={dropdownLinkClass}>Image BG Remover</Link>
                 <Link to="/video-bg-remover" onClick={handleLinkClick} className={dropdownLinkClass}>Video BG Remover</Link>
                 <Link to="/any-to-any-converter" onClick={handleLinkClick} className={dropdownLinkClass}>Any-to-Any Converter</Link>
                 <Link to="/caption-generator" onClick={handleLinkClick} className={dropdownLinkClass}>Caption Generator</Link>
                 <Link to="/mini-tools" onClick={handleLinkClick} className={dropdownLinkClass}>View All</Link>
            </div>
        )}
      </div>
      <Link to="/pricing" onClick={handleLinkClick} className={navLinkClass}>Pricing</Link>
      <Link to="/blog" onClick={handleLinkClick} className={navLinkClass}>Resources</Link>
    </>
  );

  return (
    <header className="fixed top-0 z-50 w-full" ref={headerRef}>
      <div className={`transition-all duration-1000 ease-in-out ${isScrolled ? 'pt-2 pb-2' : 'pt-6 pb-6'}`}>
        <nav
          className={`mx-auto flex items-center justify-between transition-all duration-1000 ease-in-out border
            ${isScrolled 
              ? 'max-w-fit bg-dark-card/60 backdrop-blur-4xl rounded-3xl border-white/10 shadow-2xl shadow-black/20 px-6 py-2' 
              : 'max-w-7xl bg-transparent border-transparent px-6 lg:px-8'
            }`}
          aria-label="Global"
        >
          <div className="flex flex-shrink-0">
            <Link to="/" onClick={handleLinkClick} className="-m-1.5 p-1.5 text-xl font-bold text-dark-text-primary">Oolook</Link>
          </div>
          
          <div className="flex lg:hidden">
            <button onClick={handleToggleMenu} className="text-dark-text-secondary"><FaBars size={20} /></button>
          </div>

          <div className="hidden lg:flex lg:gap-x-12 ml-16">{renderNavLinks()}</div>

          <div className="hidden lg:flex lg:flex-1 lg:justify-end items-center gap-x-6 ml-12">
            <div className={`flex items-center gap-x-6 transform transition-all duration-700 ease-in-out origin-right whitespace-nowrap ${isScrolled ? 'scale-x-0 -translate-x-8 opacity-0 w-0' : 'scale-x-100 opacity-100 w-auto'}`}>
              <a href="https://app.oolook.in/login" className="text-sm font-semibold leading-6 text-dark-text-primary hover:text-dark-text-secondary">Log in</a>
              <Link to="/schedule-demo" className="rounded-md bg-white/10 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-white/20">Schedule a demo</Link>
            </div>
            
            <a href="https://app.oolook.in/signup" className="rounded-full bg-gradient-to-r from-dark-accent-start to-dark-accent-end px-4 py-2.5 text-sm font-semibold text-white shadow-lg hover:opacity-90 transition-all duration-300">Try for free</a>
          </div>
        </nav>
      </div>
      
       <div className={`lg:hidden ${isMobileMenuOpen ? 'block' : 'hidden'}`} role="dialog" aria-modal="true">
        <div className="fixed inset-0 z-50" />
        <div className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-dark-bg px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-white/10">
          <div className="flex items-center justify-between">
            <Link to="/" onClick={handleLinkClick} className="-m-1.5 p-1.5 text-xl font-bold text-dark-text-primary">Oolook</Link>
            <button type="button" className="-m-2.5 rounded-md p-2.5 text-dark-text-secondary" onClick={handleToggleMenu}><FaTimes size={20} /></button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/25">
              <div className="space-y-2 py-6">
                <Link to="/scheduling" onClick={handleLinkClick} className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-dark-text-primary hover:bg-dark-card">Scheduling</Link>
                <Link to="/analytics" onClick={handleLinkClick} className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-dark-text-primary hover:bg-dark-card">Analytics</Link>
                <Link to="/pricing" onClick={handleLinkClick} className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-dark-text-primary hover:bg-dark-card">Pricing</Link>
                <Link to="/blog" onClick={handleLinkClick} className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-dark-text-primary hover:bg-dark-card">Resources</Link>
              </div>
              <div className="py-6">
                 <a href="https://app.oolook.in/" className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-dark-text-primary hover:bg-dark-card" onClick={handleLinkClick}>Log in</a>
                 <a href="https://app.oolook.in/signup" className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-dark-text-primary hover:bg-dark-card" onClick={handleLinkClick}>Try for free</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;